import { ApiResponseError } from '@axl/accel-framework/api';
import { InviteStatus } from '@axl/accel-framework/enums';
import { Contact } from '@axl/accel-framework/models';
import { TokenStore } from '@axl/accel-framework/stores';
import { LangDetector, isEmpty } from '@axl/accel-framework/utils';
import Api from 'api';
import { FetchContactRequest } from 'api/methods/auth/types/contact';
import { LoginAsModel, LoginAsRequest } from 'api/methods/auth/types/login-as';
import { LoginTokenRequest } from 'api/methods/auth/types/login-token';
import { LogoutRequest } from 'api/methods/auth/types/logout';
import { SendInviteRequest } from 'api/methods/auth/types/send-invite';
import { action, computed, observable, runInAction } from 'mobx';
import moment from 'moment';
import { inject } from 'react-ioc';
import { SchoolStore } from 'stores';
import { CheckInviteRequest } from '../../../api/methods/auth/types/check-invite';
import { ConfirmEmailRequest } from '../../../api/methods/auth/types/confirm-email';
import { ConfirmInviteModel, ConfirmInviteRequest } from '../../../api/methods/auth/types/confirm-invite';
import { ForgotPasswordRequest } from '../../../api/methods/auth/types/forgot-password';
import { LoginRequest } from '../../../api/methods/auth/types/login';
import { RegisterRequest } from '../../../api/methods/auth/types/register';
import { ResetPasswordRequest } from '../../../api/methods/auth/types/reset-password';

type ConfirmEmailModel = {
    token: string,
    userId: string,
}

export default class AuthStore {
    constructor(protected api: Api,
        private langDetector: LangDetector,
        private tokenStore: TokenStore,
        private schoolStore: SchoolStore) {
        this.api = api || inject(this, Api);
        this.langDetector = langDetector || inject(this, LangDetector);
        this.tokenStore = tokenStore || inject(this, TokenStore);
        this.schoolStore = schoolStore || inject(this, SchoolStore);
    }

    @observable inProgress = false;
    @observable errors: ApiResponseError[] = [];
    @observable email: string = '';
    @observable password: string = '';
    @observable confirmPassword: string = '';
    @observable urlParameters: any;
    @observable isAgreementAccepted: boolean = false;
    @observable contact: Contact | null = null;

    @action async login() {
        this.inProgress = true;
        const result = await this.api.auth.login(new LoginRequest(this.email, this.password, this.contact?.id));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false;
        });
        return result.success;
    }

    @action async logout() {
        const token = this.tokenStore.refreshToken;
        if (token == null)
            return false;

        const result = await this.api.auth.logout(new LogoutRequest(token));
        return result.success;
    }

    @action async register(inviteToken?: string) {
        this.inProgress = true;
        const result = await this.api.auth.register(new RegisterRequest({
            contactId: this.contact?.id,
            email: this.email,
            password: this.password,
            confirmPassword: this.confirmPassword,
            lang: this.schoolStore.school.lang || this.langDetector.lang,
            timezoneOffset: moment().format('ZZ'),
            urlParameters: this.urlParameters,
            isAgreementAccepted: this.isAgreementAccepted,
            inviteToken: inviteToken
        }));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success;
    }


    @action async forgotPassword() {
        this.inProgress = true;
        var result = await this.api.auth.forgotPassword(new ForgotPasswordRequest(this.email));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success;
    }

    @action async resetPassword(token: string) {
        this.inProgress = true;
        var result = await this.api.auth.resetPassword(new ResetPasswordRequest(token, this.password, this.confirmPassword));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success;
    }

    @action async confirmEmail(confirmModel: ConfirmEmailModel) {
        this.inProgress = true;
        var result = await this.api.auth.confirmEmail(new ConfirmEmailRequest(confirmModel));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success;
    }

    @action async confirmInvite(model: ConfirmInviteModel) {
        this.inProgress = true;
        var result = await this.api.auth.confirmInvite(new ConfirmInviteRequest(model));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success;
    }

    @action async checkInvite(token: string) {
        this.inProgress = true;
        var result = await this.api.auth.checkInvite(new CheckInviteRequest(token));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success ? result.response.status : InviteStatus.NotFound;
    }

    @action async sendInvite(email: string) {
        this.inProgress = true;
        var result = await this.api.auth.sendInvite(new SendInviteRequest(email));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result.success;
    }

    @action async loginToken(token: string) {
        this.inProgress = true;
        var result = await this.api.auth.loginToken(new LoginTokenRequest(token));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result;
    }

    @action async loginAs(model: LoginAsModel) {
        this.inProgress = true;
        var result = await this.api.auth.loginAs(new LoginAsRequest(model));
        runInAction(() => {
            this.errors = result.response.errors;
            this.inProgress = false
        });
        return result;
    }

    @action async fetchContact(contactId: string) {
        this.inProgress = true;
        var result = await this.api.auth.fetchContact(new FetchContactRequest(contactId));
        runInAction(() => {
            this.inProgress = false
        });
        return result;
    }

    @action setPassword(value: string): void {
        this.password = value;
    }

    @action setPasswordConfirm(value: string): void {
        this.confirmPassword = value;
    }

    @action setEmail(value: string): void {
        this.email = value?.trimSpaces();
    }

    @action setUrlParameters(urlParameters: any): void {
        this.urlParameters = urlParameters;
    }

    @action setIsAgreementAccepted(value: boolean) {
        this.isAgreementAccepted = value;
    }

    @action setContact(contact: Contact) {
        this.contact = contact;
        this.setEmail(contact.email);
    }

    @computed get hasContactEmail() {
        return !isEmpty(this.contact?.email);
    }
}